import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomKeyboard from "./CustomKeyboard";
import {
  convertInchesToFeet,
  parseFractionalInches,
} from "../../functions/calculation";
import { useNavigate, useParams } from "react-router-dom";
import { Backdrop, CircularProgress } from "@mui/material";
import {
  fetchMeasurementData,
  fetchMeasurementDataEdit,
  updateMeasurement,
} from "../../redux/actions/measurementActions";
import { useDebouncedCallback } from "../../functions/debounceHook";
import DynamicTable from "./Reusables/DynamicTable";
import { dealerFetchSettingsAction } from "../../redux/actions/userSettings";
import {
  fetchInventoryByCategory,
  fetchInventoryLiftStyle,
} from "../../redux/actions/orderActions";
import CostMetricsCard from "./Reusables/CostAndSqft";
import useAlert from "./Reusables/UseAlert";

const MeasurementEditForm = ({ productType, fabrics }) => {
  const { dealerInfo } = useSelector((state) => state.dealerLogin);
  const settings = useSelector((state) => state.userSettings);
  const { settings: settingsData } = settings;
  const { liftStyleInventory, loading: liftstyleLoading, error: liftstyleError } = useSelector(
    (state) => state.liftStyleInventory
  );
  const inventoryByCategory = useSelector((state) => state.inventoryByCategory);
  const { inventory } = inventoryByCategory;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { showAlert, alertElement } = useAlert();
  const tableContainerRef = useRef(null);
  const inputRefs = useRef([]);

  const [tableData, setTableData] = useState([
    {
      width: "",
      height: "",
      sqft: 0,
      remark: "",
      location: "",
      liftStyle: "",
      productMaterial: "",
      productHeadrail: "",
      sleeveOption: "",
    },
  ]);
  const [totalCost, setTotalCost] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [totalSqft, setTotalSqft] = useState(0);
  const [focusedIndex, setFocusedIndex] = useState(null);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(true);
  const [measurementId, setMeasurementId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tag, setTag] = useState("");
  const tableDataRef = useRef(tableData);
  tableDataRef.current = tableData;

  useEffect(() => {
    const handleResize = () => {
      if (window.matchMedia("(max-width: 1000px)").matches) {
        setIsReadOnly(true);
      } else {
        setIsReadOnly(false);
        setIsKeyboardVisible(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (id) {
      fetchMeasurementDataEdit(
        id,
        dealerInfo.token,
        setTableData,
        setTotalCost,
        setTag
      );
      setMeasurementId(id);
    }
  }, [id, dealerInfo.token]);

  useEffect(() => {
    if (productType) {
      dispatch(fetchInventoryByCategory(productType, dealerInfo.token));
      dispatch(fetchInventoryLiftStyle(productType, dealerInfo.token));
    }
  }, [productType, dealerInfo.token, dispatch]);

  useEffect(() => {
    if (focusedIndex !== null && inputRefs.current[focusedIndex.row]) {
      const input = inputRefs.current[focusedIndex.row][focusedIndex.field];
      if (input) {
        input.focus();
        input.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [focusedIndex]);

  const debouncedUpdateMeasurement = useDebouncedCallback(async () => {
    if (measurementId && hasUnsavedChanges) {
      try {
        await updateMeasurement(
          measurementId,
          productType,
          tag,
          tableDataRef.current,
          dealerInfo.token,
          totalSqft,
          undefined, 
          showAlert
        );
        setHasUnsavedChanges(false);
        fetchMeasurementData(
          measurementId,
          dealerInfo.token,
          setTableData,
          tableData,
          hasUnsavedChanges,
          setTotalSqft,
          setTotalCost
        );
      } catch (error) {
        console.error("Error in debounced update:", error);
        // showAlert("Failed to auto-save measurement", "error");
      }
    }
  }, 2000);

  useEffect(() => {
    if (measurementId) {
      const intervalId = setInterval(() => {
        debouncedUpdateMeasurement();
      }, 3000);
      return () => clearInterval(intervalId);
    }
  }, [debouncedUpdateMeasurement, measurementId]);

  useEffect(() => {
    const calculatedTotalSqft = tableData.reduce(
      (acc, row) => acc + (row.sqft || 0),
      0
    );
    setTotalSqft(calculatedTotalSqft);
  }, [tableData]);

  const handleInputChange = (index, field, value) => {
    const newData = [...tableData];
    newData[index][field] = value;
    if (field === "productHeadrail") {
      // Reset sleeveOption if headrail changes
      newData[index]["sleeveOption"] = "";
    }
    setTableData(newData);
    setHasUnsavedChanges(true);

    const isCurrentRowComplete =
      newData[index].width?.trim() !== "" &&
      newData[index].height?.trim() !== "" &&
      newData[index].liftStyle?.trim() !== "" &&
      newData[index].productMaterial?.trim() !== "" &&
      newData[index].productHeadrail?.trim() !== "";

    if (isCurrentRowComplete) {
      debouncedUpdateMeasurement();
    }

    if (isCurrentRowComplete && index === newData.length - 1) {
      setTableData([
        ...newData,
        {
          width: "",
          height: "",
          sqft: 0,
          remark: "",
          location: "",
          liftStyle: newData[index].liftStyle || "",
          productMaterial: newData[index].productMaterial || "",
          productHeadrail: newData[index].productHeadrail || "",
          sleeveOption: newData[index].sleeveOption || "",
        },
      ]);
    }

    if (
      newData.length > 1 &&
      index === newData.length - 1 &&
      (!newData[index].width || newData[index].width.trim() === "") &&
      (!newData[index].height || newData[index].height.trim() === "") &&
      (!newData[index].liftStyle || newData[index].liftStyle.trim() === "") &&
      (!newData[index].productMaterial || newData[index].productMaterial.trim() === "") &&
      (!newData[index].productHeadrail || newData[index].productHeadrail.trim() === "")
    ) {
      setTableData(newData.slice(0, -1));
    }
  };

  const handleKeyPress = (key) => {
    if (focusedIndex !== null) {
      const { row, field } = focusedIndex;
      const currentValue = tableData[row][field];
      const keyString = String(key);

      if (keyString.includes("/")) {
        const parts = currentValue.split(" ");
        if (parts.length > 1) {
          parts[parts.length - 1] = keyString;
        } else {
          parts.push(keyString);
        }
        const newValue = parts.join(" ");
        handleInputChange(row, field, newValue);
      } else {
        const newValue = currentValue + keyString;
        handleInputChange(row, field, newValue);
      }
    }
  };

  const handleNext = () => {
    if (focusedIndex !== null) {
      const { row, field } = focusedIndex;
      if (field === "width") {
        setFocusedIndex({ row, field: "height" });
      } else if (field === "height") {
        setFocusedIndex({ row, field: "productHeadrail" });
      } else if (field === "productHeadrail") {
        setFocusedIndex({ row, field: "sleeveOption" });
      } else if (field === "sleeveOption") {
        setFocusedIndex({ row, field: "productMaterial" });
      } else if (field === "productMaterial") {
        setFocusedIndex({ row, field: "liftStyle" });
      } else if (field === "liftStyle" && row < tableData.length - 1) {
        setFocusedIndex({ row: row + 1, field: "width" });
      }
    }
  };

  const handlePrevious = () => {
    if (focusedIndex !== null) {
      const { row, field } = focusedIndex;
      if (field === "liftStyle") {
        setFocusedIndex({ row, field: "productMaterial" });
      } else if (field === "productMaterial") {
        setFocusedIndex({ row, field: "sleeveOption" });
      } else if (field === "sleeveOption") {
        setFocusedIndex({ row, field: "productHeadrail" });
      } else if (field === "productHeadrail") {
        setFocusedIndex({ row, field: "height" });
      } else if (field === "height") {
        setFocusedIndex({ row, field: "width" });
      } else if (field === "width" && row > 0) {
        setFocusedIndex({ row: row - 1, field: "liftStyle" });
      }
    }
  };

  const handleEnter = () => {
    if (focusedIndex !== null) {
      const { row } = focusedIndex;
      if (row < tableData.length - 1) {
        setFocusedIndex({ row: row + 1, field: "width" });
      } else {
        setTableData([
          ...tableData,
          {
            width: "",
            height: "",
            sqft: 0,
            remark: "",
            location: "",
            liftStyle: "",
            productMaterial: "",
            productHeadrail: "",
            sleeveOption: "",
          },
        ]);
        setFocusedIndex({ row: row + 1, field: "width" });
      }
    }
  };

  const handleClear = () => {
    if (focusedIndex !== null) {
      const { row, field } = focusedIndex;
      handleInputChange(row, field, "");
    }
  };

  const handleFocus = (row, field) => {
    if (field === "width" || field === "height") {
      setFocusedIndex({ row, field });
      if (window.matchMedia("(max-width: 1000px)").matches) {
        setIsKeyboardVisible(true);
      }
    } else {
      setIsKeyboardVisible(false);
    }
  };

  const handleCloseKeyboard = () => {
    setIsKeyboardVisible(false);
  };

  const handleAddRow = (index) => {
    const newRow = {
      width: "",
      height: "",
      sqft: 0,
      remark: "",
      location: "",
      liftStyle: "",
      productMaterial: "",
      productHeadrail: "",
      sleeveOption: "",
    };
    setTableData((prevData) => [
      ...prevData.slice(0, index + 1),
      newRow,
      ...prevData.slice(index + 1),
    ]);
    setHasUnsavedChanges(true);
  };

  const handleDeleteRow = (index) => {
    setTableData((prevData) => {
      const updatedData = prevData.filter((_, i) => i !== index);
      const calculatedTotalSqft = updatedData.reduce(
        (acc, row) => acc + (row.sqft || 0),
        0
      );
      setTotalSqft(calculatedTotalSqft);
      return updatedData;
    });
    setHasUnsavedChanges(true);
  };

  useEffect(() => {
    dispatch(dealerFetchSettingsAction(dealerInfo.token));
  }, [dispatch, dealerInfo.token]);

  const measurementColumns = [
    {
      header: (
        <>
          Width <span style={{ color: "red" }}>*</span>
        </>
      ),
      key: "width",
      inputClass: "",
      width: "100px",
      focusable: true,
    },
    {
      header: (
        <>
          Height <span style={{ color: "red" }}>*</span>
        </>
      ),
      key: "height",
      inputClass: "",
      width: "100px",
      focusable: true,
    },
    {
      header: (
        <>
          Headrail <span style={{ color: "red" }}>*</span>
        </>
      ),
      key: "productHeadrail",
      inputClass: "",
      width: "150px",
      focusable: true,
    },
    {
      header: "Sleeve Option",
      key: "sleeveOption",
      inputClass: "",
      width: "150px",
      focusable: true,
    },
    {
      header: (
        <>
          Fabric <span style={{ color: "red" }}>*</span>
        </>
      ),
      key: "productMaterial",
      inputClass: "",
      width: "150px",
      focusable: true,
    },
    {
      header: (
        <>
          LiftStyle <span style={{ color: "red" }}>*</span>
        </>
      ),
      key: "liftStyle",
      inputClass: "",
      width: "100px",
      focusable: true,
    },
    {
      header: "Sqft",
      key: "sqft",
      inputClass: "",
      width: "70px",
      readOnly: true,
    },
    {
      header: "Cost",
      key: "payment",
      inputClass: "",
      width: "70px",
      readOnly: true,
    },
    ...(settingsData?.showLocation
      ? [{ header: "Location", key: "location", inputClass: "" }]
      : []),
    ...(settingsData?.showRemark
      ? [{ header: "Remarks", key: "remark", inputClass: "" }]
      : []),
    ...(settingsData?.showMountLift
      ? [{ header: "Lift", key: "lift", inputClass: "" }]
      : []),
    ...(settingsData?.showControl
      ? [{ header: "Control", key: "control", inputClass: "" }]
      : []),
    ...(settingsData?.showTile
      ? [{ header: "Tile", key: "tile", inputClass: "" }]
      : []),
  ];

  const tableContainerStyles = {
    maxHeight: "100%",
    overflowY: "auto",
    position: "relative",
    transition: "padding-bottom 0.3s ease",
    paddingBottom: isKeyboardVisible ? "100px" : "0px",
  };

  return (
    <div className="container mt-5 mb-4">
      {alertElement}
      <div className="row">
        <div className="col-lg-10">
          <h6 className="mb-2 text-uppercase">Edit Measurement Info</h6>
        </div>
      </div>
      <CostMetricsCard
        totalSqft={totalSqft}
        totalCost={totalCost}
        header={"Estimated Cost"}
      />
      <div
        className="table-responsive pb-5"
        style={tableContainerStyles}
        ref={tableContainerRef}
      >
        <div style={{ overflowX: "auto" }}>
          <DynamicTable
            tableData={tableData}
            handleInputChange={handleInputChange}
            handleAddRow={handleAddRow}
            handleDeleteRow={handleDeleteRow}
            columns={measurementColumns}
            handleFocus={handleFocus}
            inputRefs={inputRefs}
            isReadOnly={isReadOnly}
            liftStyleInventory={liftStyleInventory}
            fabrics={fabrics}
            inventory={inventory}
          />
        </div>
      </div>
      {isKeyboardVisible && (
        <CustomKeyboard
          onKeyPress={handleKeyPress}
          onNext={handleNext}
          onPrevious={handlePrevious}
          onEnter={handleEnter}
          onClear={handleClear}
          onClose={handleCloseKeyboard}
        />
      )}
      {loading && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <div className="d-md-none" style={{ height: "50vh" }}></div>
    </div>
  );
};

export default MeasurementEditForm;